import { useRef, useState } from "react";
import { UploadCloud, ImageIcon, X, Camera, FolderOpen } from "lucide-react";
import { ACCEPTED_IMG, MAX_IMG_MB } from "@/lib/image";
import { toast } from "sonner";

/*
 * Zone de dépôt réutilisable (packaging / photo produit / affiche).
 * - Desktop : drag & drop + clic pour parcourir.
 * - Mobile : boutons dédiés "Prendre en photo" (caméra arrière) + "Parcourir" (galerie/fichiers).
 */
export default function ImageDrop({ onFile, file, disabled = false, testid = "image-drop", hint }) {
  const inputRef = useRef(null);       // galerie / fichiers
  const cameraRef = useRef(null);      // caméra native mobile (capture="environment")
  const [over, setOver] = useState(false);
  const [preview, setPreview] = useState(null);

  const accept = (f) => {
    if (!f) return;
    if (!ACCEPTED_IMG.includes(f.type)) return toast.error("Format non supporté (PNG, JPG, WEBP)");
    if (f.size > MAX_IMG_MB * 1024 * 1024) return toast.error(`Image trop volumineuse (max ${MAX_IMG_MB} Mo)`);
    setPreview(URL.createObjectURL(f));
    onFile(f);
  };

  const clear = (e) => {
    e.stopPropagation();
    setPreview(null);
    onFile(null);
    if (inputRef.current) inputRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const openCamera = (e) => { e.stopPropagation(); if (!disabled) cameraRef.current?.click(); };
  const openBrowse = (e) => { e.stopPropagation(); if (!disabled) inputRef.current?.click(); };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testid}
      onClick={openBrowse}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !disabled) inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) accept(e.dataTransfer.files?.[0]); }}
      className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-colors duration-200 p-5 sm:p-8 text-center
        ${over ? "border-primary bg-accent" : "border-border bg-white hover:border-primary/50"}
        ${disabled ? "opacity-60 pointer-events-none" : ""}`}
    >
      {/* Input galerie / fichiers */}
      <input ref={inputRef} type="file" accept={ACCEPTED_IMG.join(",")} className="hidden"
        data-testid={`${testid}-input`} onChange={(e) => accept(e.target.files?.[0])} />
      {/* Input caméra mobile (capture=environment → caméra arrière) */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid={`${testid}-camera-input`} onChange={(e) => accept(e.target.files?.[0])} />

      {preview ? (
        <div className="relative inline-block">
          <img src={preview} alt="aperçu" className="max-h-56 sm:max-h-64 rounded-lg mx-auto shadow-sm" data-testid={`${testid}-preview`} />
          <button onClick={clear} data-testid={`${testid}-clear`}
            className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white border border-border shadow flex items-center justify-center hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
          <p className="mt-3 text-xs font-mono text-muted-foreground truncate max-w-[280px] mx-auto">{file?.name}</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 py-2 sm:py-4">
          <div className="w-14 h-14 rounded-full bg-accent text-primary flex items-center justify-center">
            <UploadCloud className="w-6 h-6" strokeWidth={1.8} />
          </div>
          {/* Message desktop */}
          <p className="hidden sm:block font-serif text-lg text-foreground">Glissez-déposez une image ici</p>
          {/* Message mobile */}
          <p className="sm:hidden font-serif text-base text-foreground">Photo du packaging ou visuel</p>

          <p className="text-sm text-muted-foreground max-w-sm">{hint || "Packaging, photo de produit ou affiche."}</p>

          {/* Boutons mobile — prendre photo + parcourir */}
          <div className="grid grid-cols-2 gap-2 w-full max-w-xs mt-1 sm:hidden">
            <button
              type="button"
              onClick={openCamera}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary text-white text-sm font-semibold py-2.5 hover:bg-[hsl(var(--primary-light))] active:scale-95 transition"
              data-testid={`${testid}-take-photo`}
            >
              <Camera className="w-4 h-4" /> Prendre
            </button>
            <button
              type="button"
              onClick={openBrowse}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-white border border-border text-sm font-semibold py-2.5 hover:border-primary hover:text-primary active:scale-95 transition"
              data-testid={`${testid}-browse`}
            >
              <FolderOpen className="w-4 h-4" /> Parcourir
            </button>
          </div>

          {/* Indication desktop : cliquer pour parcourir */}
          <p className="hidden sm:block text-xs text-muted-foreground">ou cliquez n'importe où pour parcourir.</p>

          <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground mt-1">
            <ImageIcon className="w-3.5 h-3.5" /> PNG · JPG · WEBP · max {MAX_IMG_MB} Mo
          </span>
        </div>
      )}
    </div>
  );
}
